import { useEffect, useState } from "react";
import axios from "axios";
import { Play } from "lucide-react";
import VideoModal from "@/components/VideoModal";
import { API, fileUrl } from "@/lib/api";

const FALLBACK = [
    {
        id: "p1",
        poster_url: "/portfolio/v1-resto-grec.jpg",
        video_url: "https://customer-assets-39nsmqrw.emergentagent.net/job_creator-melanie-pro/artifacts/83f214db7c30c1fb_resto%20grec%20%281%29.mp4",
        title: "Restaurant grec",
        category: "Food",
    },
    {
        id: "p2",
        poster_url: "/portfolio/v2-body-house.jpg",
        video_url: "https://customer-assets-39nsmqrw.emergentagent.net/job_creator-melanie-pro/artifacts/975e9c8a6b552646_video%201.mp4",
        title: "Body House",
        category: "Boutique",
    },
    {
        id: "p3",
        poster_url: "/portfolio/v3-chais-monet.jpg",
        video_url: "https://customer-assets-39nsmqrw.emergentagent.net/job_creator-melanie-pro/artifacts/bd0172eb563f04ba_video%202.mp4",
        title: "Hôtel Chais Monet",
        category: "Hôtellerie",
    },
    {
        id: "p4",
        poster_url: "/portfolio/v4-santosha.jpg",
        video_url: "https://customer-assets-39nsmqrw.emergentagent.net/job_creator-melanie-pro/artifacts/61b9ad3c1db75e06_video%203.mp4",
        title: "Restaurant Santosha",
        category: "Food",
    },
    {
        id: "p5",
        poster_url: "/portfolio/v5-club-med.jpg",
        video_url: "https://customer-assets-39nsmqrw.emergentagent.net/job_creator-melanie-pro/artifacts/027e19f05afde26f_video%204.mp4",
        title: "Club Med Marrakech",
        category: "Hôtellerie",
    },
];

export default function PortfolioGrid() {
    const [videos, setVideos] = useState(FALLBACK);
    const [active, setActive] = useState(null);

    useEffect(() => {
        axios
            .get(`${API}/videos`)
            .then((r) => {
                if (Array.isArray(r.data) && r.data.length > 0) {
                    setVideos(
                        r.data.map((v) => ({
                            ...v,
                            video_url: fileUrl(v.video_url),
                            poster_url: fileUrl(v.poster_url),
                        }))
                    );
                }
            })
            .catch(() => {});
    }, []);

    return (
        <section
            id="portfolio"
            data-testid="portfolio-section"
            className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#FAFAFA]"
        >
            <div className="max-w-[1600px] mx-auto">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
                    <div>
                        <p className="eyebrow mb-5">Portfolio</p>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-ink">
                            Création de contenu <span className="italic">UGC</span>.
                        </h2>
                    </div>
                    <p className="text-[#4A4A4A] max-w-md text-base">
                        Une sélection récente : food, restaurants, skincare, lifestyle et
                        expériences filmées à Bordeaux et ailleurs.
                    </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                    {videos.map((v, i) => (
                        <button
                            type="button"
                            key={v.id || i}
                            onClick={() => setActive(v)}
                            data-testid={`portfolio-card-${i}`}
                            className="group relative aspect-[9/16] overflow-hidden block bg-bordeaux text-left"
                        >
                            {v.poster_url ? (
                                <img
                                    src={v.poster_url}
                                    alt={`${v.title} — vidéo UGC ${v.category} par Mélanie Perez`}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    loading="lazy"
                                />
                            ) : (
                                <video
                                    src={`${v.video_url}#t=0.5`}
                                    muted
                                    playsInline
                                    preload="metadata"
                                    onLoadedMetadata={(e) => {
                                        try { e.currentTarget.currentTime = 0.5; } catch {}
                                    }}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 pointer-events-none bg-bordeaux"
                                    aria-label={`${v.title} — aperçu`}
                                />
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                <span className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white/25 backdrop-blur-md border border-white/40 flex items-center justify-center">
                                    <Play className="w-5 h-5 md:w-6 md:h-6 text-white fill-white ml-0.5" />
                                </span>
                            </div>
                            <div className="absolute left-4 bottom-4 right-4 text-white">
                                <p className="text-[10px] uppercase tracking-[0.25em] text-gold">
                                    {v.category}
                                </p>
                                <p className="font-display text-xl md:text-2xl mt-1 leading-tight">
                                    {v.title}
                                </p>
                            </div>
                        </button>
                    ))}
                </div>

                <div className="mt-14 text-center">
                    <a
                        href="https://www.instagram.com/melanie.creatrice/"
                        target="_blank"
                        rel="noreferrer"
                        data-testid="portfolio-see-more"
                        className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-ink hover:text-fuchsia transition-colors"
                    >
                        Voir plus sur Instagram
                        <span className="w-8 h-px bg-current" />
                    </a>
                </div>
            </div>

            <VideoModal
                open={!!active}
                video={active}
                onClose={() => setActive(null)}
            />
        </section>
    );
}
